// @ts-check
// Layout das telas de autenticação: painel de marca à esquerda (some no celular) e o formulário centralizado à
// direita, com no máximo 380 px. No host da instituição o painel mostra a escola, com "via Matrícula Mais".
import { html } from 'htm/preact';
import { Icon } from '../../ui/icon.js';
import { stringToThemeColor } from '../../lib/theme-colors.js';
import { t } from '../../i18n/index.js';
import { appEnvironmentState } from '../../lib/app-environment.js';
import { BrandLogo } from '../../ui/brand-logo.js';

/**
 * @typedef {{ displayName?: string | null, logoUrl?: string | null }} AuthTenant
 */

const BENEFITS = [
  { icon: 'IconLayoutKanban', key: 'funnel' },
  { icon: 'IconBrandWhatsapp', key: 'conversations' },
  { icon: 'IconSchool', key: 'enrollments' },
];

/**
 * Logo da instituição (imagem de /auth/tenant) ou a inicial sobre a cor determinística do nome.
 * @param {{ tenant: AuthTenant, size: number }} props
 */
function TenantLogo({ tenant, size }) {
  const name = tenant.displayName ?? '';
  if (tenant.logoUrl) {
    return html`<img class="auth-tenant-logo" src=${tenant.logoUrl} alt="" width=${size} height=${size} style=${{ width: `${size}px`, height: `${size}px` }} />`;
  }
  const color = stringToThemeColor(name);
  return html`<span
    class="auth-tenant-logo"
    aria-hidden="true"
    style=${{ width: `${size}px`, height: `${size}px`, fontSize: `${Math.round(size * 0.45)}px`, background: `var(--t-color-${color}4)`, color: `var(--t-color-${color}12)` }}
    >${name.trim().charAt(0).toUpperCase()}</span
  >`;
}

/**
 * Identidade exibida no topo: a instituição (no host dela) ou o Matrícula Mais (no portal).
 * @param {{ tenant?: AuthTenant | null, compact?: boolean }} props
 */
function Identity({ tenant, compact = false }) {
  if (tenant?.displayName) {
    return html`<div class="auth-identity" data-compact=${compact ? 'true' : undefined}>
      <${TenantLogo} tenant=${tenant} size=${compact ? 32 : 40} />
      <span class="auth-identity-text">
        <span class="auth-identity-name">${tenant.displayName}</span>
        <span class="auth-identity-via">${t('auth.brand.via')}</span>
      </span>
    </div>`;
  }
  // no painel marinho o logotipo é o branco; no topo do celular, sobre o bege do formulário, o azul
  return html`<div class="auth-identity" data-compact=${compact ? 'true' : undefined}>
    <${BrandLogo} height=${compact ? 24 : 30} label=${t('auth.brand.name')} tone=${compact ? 'light' : 'dark'} />
  </div>`;
}

/**
 * @param {{ title: string, subtitle?: any, tenant?: AuthTenant | null, children: any, footer?: any }} props
 */
export function AuthLayout({ title, subtitle, tenant, children, footer }) {
  const year = new Date().getFullYear();
  return html`<div class="auth-page">
    <aside class="auth-brand-panel">
      <${Identity} tenant=${tenant} />
      <div class="auth-brand-body">
        <h2 class="auth-brand-tagline">${t('auth.brand.tagline')}</h2>
        <p class="auth-brand-description">${t('auth.brand.description')}</p>
        <ul class="auth-benefits">
          ${BENEFITS.map(
            (benefit) => html`<li class="auth-benefit" key=${benefit.key}>
              <span class="auth-benefit-icon"><${Icon} name=${benefit.icon} size=${18} stroke=${1.8} /></span>
              <span class="auth-benefit-text">
                <span class="auth-benefit-title">${t(`auth.brand.benefits.${benefit.key}.title`)}</span>
                <span class="auth-benefit-description">${t(`auth.brand.benefits.${benefit.key}.description`)}</span>
              </span>
            </li>`,
          )}
        </ul>
      </div>
    </aside>
    <main class="auth-main light">
      <div class="auth-main-content">
        ${appEnvironmentState.value !== 'production'
          ? html`<p class="auth-environment-banner" role="note" data-environment=${appEnvironmentState.value}>${t(`auth.environment.${appEnvironmentState.value}`)}</p>`
          : null}
        <div class="auth-mobile-identity"><${Identity} tenant=${tenant} compact=${true} /></div>
        <h1 class="auth-title">${title}</h1>
        ${subtitle ? html`<p class="auth-subtitle">${subtitle}</p>` : null}
        ${children}
        ${footer ? html`<div class="auth-footer">${footer}</div>` : null}
      </div>
      <footer class="auth-legal">
        <span>${t('auth.legal.copyright', { year })}</span>
        <span class="auth-legal-links">
          <a href="#">${t('auth.legal.privacy')}</a>
          <a href="#">${t('auth.legal.terms')}</a>
        </span>
      </footer>
    </main>
  </div>`;
}

/**
 * @param {{ label: string, name: string, type?: string, value: string, onInput: (value: string) => void, placeholder?: string,
 *   autoComplete?: string, error?: string | null, hint?: string, autoFocus?: boolean, required?: boolean }} props
 */
export function AuthTextField({ label, name, type = 'text', value, onInput, placeholder, autoComplete, error, hint, autoFocus = false, required = true }) {
  return html`<label class="auth-field">
    <span class="input-label">${label}</span>
    <input
      class="text-input auth-text-input"
      data-error=${error ? 'true' : undefined}
      name=${name}
      type=${type}
      value=${value}
      placeholder=${placeholder}
      autocomplete=${autoComplete}
      required=${required}
      ref=${(element) => {
        if (autoFocus && element && !element.dataset.focused) {
          element.dataset.focused = 'true';
          element.focus();
        }
      }}
      onInput=${(event) => onInput(event.currentTarget.value)}
    />
    ${error ? html`<span class="input-hint" data-danger="true">${error}</span>` : hint ? html`<span class="input-hint">${hint}</span>` : null}
  </label>`;
}
